import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type ProjectMember } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';

const PROJECT_MEMBERS = 'projectMembers';

export const membershipId = (projectId: string, uid: string): string => `${projectId}_${uid}`;

const memberConverter = buildConverter<ProjectMember>(
  (data) =>
    ({
      id: data.id,
      projectId: data.projectId,
      uid: data.uid,
      email: data.email,
      displayName: data.displayName,
      projectRole: data.projectRole,
      status: data.status,
      joinedAt: toTimestampString(data.joinedAt) ?? new Date().toISOString(),
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
    }) as ProjectMember,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreProjectMemberRepository {
  async findById(id: string): Promise<ProjectMember | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, PROJECT_MEMBERS, id).withConverter(memberConverter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<ProjectMember[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, PROJECT_MEMBERS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(memberConverter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findByUserId(uid: string): Promise<ProjectMember[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, PROJECT_MEMBERS),
      where('uid', '==', uid),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(memberConverter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findByProjectAndUser(projectId: string, uid: string): Promise<ProjectMember | undefined> {
    return this.findById(membershipId(projectId, uid));
  }

  async save(member: ProjectMember): Promise<ProjectMember> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const id = membershipId(member.projectId, member.uid);
    const existing = await this.findById(id);
    const data = { ...member, id, environment: getCurrentEnvironment() };
    if (existing) {
      await updateDoc(doc(db, PROJECT_MEMBERS, id), { ...data, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, PROJECT_MEMBERS, id).withConverter(memberConverter), data);
    }
    return { ...data, createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, PROJECT_MEMBERS, id));
  }
}

export const createFirestoreProjectMemberRepository = (): FirestoreProjectMemberRepository =>
  new FirestoreProjectMemberRepository();
